/* Tundra theme — storefront scripts (no dependencies) */
(() => {
  const theme = window.theme || {};
  const isRTL = document.documentElement.dir === 'rtl';

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const storage = {
    get(key, fallback) { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch (e) { return fallback; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ } },
  };

  /* ------------------------------------------------------------------
     Quick view (declared early: other components call it)
  ------------------------------------------------------------------ */
  const QuickView = {
    get modal() { return document.querySelector('[data-quick-view-modal]'); },
    async open(url, trigger) {
      const modal = this.modal;
      if (!modal) { window.location.href = url; return; }
      const content = modal.querySelector('[data-quick-view-content]');
      content.innerHTML = '';
      modal.hidden = false;
      document.body.classList.add('is-locked');
      this.trigger = trigger;
      try {
        const res = await fetch(`${url.split('?')[0]}?section_id=quick-view`);
        const doc = parseHTML(await res.text());
        const section = doc.getElementById('shopify-section-quick-view');
        if (!section) throw new Error('no quick view');
        content.innerHTML = section.innerHTML;
        modal.querySelector('[data-quick-view-close]').focus();
      } catch (err) {
        window.location.href = url;
      }
    },
    close() {
      const modal = this.modal;
      if (!modal || modal.hidden) return;
      modal.hidden = true;
      modal.querySelector('[data-quick-view-content]').innerHTML = '';
      if (!document.querySelector('.drawer:not([hidden])')) document.body.classList.remove('is-locked');
      this.trigger?.focus();
    },
  };

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (trigger) { e.preventDefault(); QuickView.open(trigger.dataset.quickView, trigger); return; }
    if (e.target.closest('[data-quick-view-close]')) QuickView.close();
  });

  /* ------------------------------------------------------------------
     Recently viewed (handles kept in localStorage)
  ------------------------------------------------------------------ */
  const RecentlyViewed = {
    key: 'tundra:recently-viewed',
    remember(productUrl) {
      const handle = (productUrl || '').split('/products/')[1]?.split(/[?#]/)[0];
      if (!handle) return;
      const list = storage.get(this.key, []).filter((h) => h !== handle);
      list.unshift(handle);
      storage.set(this.key, list.slice(0, 12));
    },
    list(exclude) { return storage.get(this.key, []).filter((h) => h !== exclude); },
  };

  /* ------------------------------------------------------------------
     Scroll reveal
  ------------------------------------------------------------------ */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -60px 0px' })
    : null;

  function initReveal(root = document) {
    root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-revealed');
    });
  }

  /* ------------------------------------------------------------------
     Drawers (menu, search, cart, filters)
  ------------------------------------------------------------------ */
  const overlay = () => document.querySelector('[data-overlay]');
  let activeDrawer = null;
  let lastFocus = null;

  function openDrawer(name) {
    const drawer = document.querySelector(`[data-drawer="${name}"]`);
    if (!drawer) return false;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(false);
    lastFocus = document.activeElement;
    drawer.hidden = false;
    drawer.classList.add('is-entering');
    requestAnimationFrame(() => requestAnimationFrame(() => drawer.classList.remove('is-entering')));
    overlay().hidden = false;
    document.body.classList.add('is-locked');
    activeDrawer = drawer;
    const focusTarget = drawer.querySelector('input[type="search"], [data-drawer-close]');
    if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
    return true;
  }

  function closeDrawer(restoreFocus = true) {
    if (!activeDrawer) return;
    const drawer = activeDrawer;
    drawer.classList.add('is-entering');
    activeDrawer = null;
    setTimeout(() => { drawer.hidden = true; drawer.classList.remove('is-entering'); }, 300);
    overlay().hidden = true;
    document.body.classList.remove('is-locked');
    if (restoreFocus && lastFocus) lastFocus.focus();
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      if (openDrawer(opener.dataset.drawerOpen)) e.preventDefault();
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.matches('[data-overlay]')) {
      closeDrawer();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDrawer();
      QuickView.close();
      document.querySelectorAll('.modal-trigger[open], .hotspot[open]').forEach((d) => d.removeAttribute('open'));
    }
    // Simple focus trap
    if (e.key === 'Tab' && activeDrawer) {
      const focusables = activeDrawer.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, summary');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  // Close size-chart modal when clicking its backdrop
  document.addEventListener('click', (e) => {
    if (!e.target.classList.contains('modal')) return;
    const details = e.target.closest('details');
    if (details) details.removeAttribute('open');
    else if (e.target.matches('[data-quick-view-modal]')) QuickView.close();
  });

  /* ------------------------------------------------------------------
     Sticky header
  ------------------------------------------------------------------ */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.onScroll = () => {
        const y = window.scrollY;
        this.classList.toggle('is-scrolled', y > 20);
        this.lastY = y;
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ------------------------------------------------------------------
     Announcement rotator
  ------------------------------------------------------------------ */
  class AnnouncementSlider extends HTMLElement {
    connectedCallback() {
      const items = this.querySelectorAll('.announcement__item');
      if (items.length < 2) return;
      let i = 0;
      const delay = (parseInt(this.dataset.autoplay, 10) || 5) * 1000;
      this.timer = setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, delay);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('announcement-slider', AnnouncementSlider);

  /* ------------------------------------------------------------------
     Slider (native scroll-snap + arrow buttons)
  ------------------------------------------------------------------ */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      if (!this.track) return;
      const step = (dir) => {
        const slide = this.track.querySelector('.slider__slide');
        const amount = slide ? slide.getBoundingClientRect().width + 24 : this.track.clientWidth * 0.8;
        this.track.scrollBy({ left: dir * amount * (isRTL ? -1 : 1), behavior: 'smooth' });
      };
      this.prev?.addEventListener('click', () => step(-1));
      this.next?.addEventListener('click', () => step(1));
      this.update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const pos = Math.abs(this.track.scrollLeft);
        if (this.prev) this.prev.disabled = pos <= 2;
        if (this.next) this.next.disabled = pos >= max - 2;
        const nav = this.querySelector('.slider__nav');
        if (nav) nav.hidden = max <= 2;
      };
      this.track.addEventListener('scroll', debounce(this.update, 60), { passive: true });
      window.addEventListener('resize', debounce(this.update, 150));
      this.update();
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ------------------------------------------------------------------
     Countdown timer
  ------------------------------------------------------------------ */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const end = new Date(this.dataset.end.trim().replace(' ', 'T')).getTime();
      if (Number.isNaN(end)) { this.hidden = true; return; }
      const els = {
        d: this.querySelector('[data-days]'), h: this.querySelector('[data-hours]'),
        m: this.querySelector('[data-minutes]'), s: this.querySelector('[data-seconds]'),
      };
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const diff = end - Date.now();
        if (diff <= 0) {
          clearInterval(this.timer);
          this.innerHTML = `<p>${this.dataset.expired}</p>`;
          return;
        }
        els.d.textContent = pad(Math.floor(diff / 864e5));
        els.h.textContent = pad(Math.floor((diff % 864e5) / 36e5));
        els.m.textContent = pad(Math.floor((diff % 36e5) / 6e4));
        els.s.textContent = pad(Math.floor((diff % 6e4) / 1e3));
      };
      tick();
      this.timer = setInterval(tick, 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ------------------------------------------------------------------
     Predictive search
  ------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-predictive-input]');
      this.results = this.querySelector('[data-predictive-results]');
      this.input?.addEventListener('input', debounce(() => this.search(), 300));
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = ''; return; }
      this.abort?.abort();
      this.abort = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.abort.signal });
        const doc = parseHTML(await res.text());
        const section = doc.querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = section ? section.innerHTML : '';
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------
     Cart
  ------------------------------------------------------------------ */
  const Cart = {
    sectionIds() {
      const ids = [];
      document.querySelectorAll('[data-cart-contents]').forEach((el) => {
        const section = el.closest('.shopify-section');
        if (section) ids.push(section.id.replace('shopify-section-', ''));
      });
      return [...new Set(ids)];
    },

    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const current = document.getElementById(`shopify-section-${id}`);
        if (!current) return;
        const doc = parseHTML(html);
        const fresh = doc.getElementById(`shopify-section-${id}`);
        if (!fresh) return;
        const drawer = current.querySelector('cart-drawer');
        const freshDrawer = fresh.querySelector('cart-drawer');
        if (drawer && freshDrawer) {
          // Keep the drawer element (and its open state) — only swap its content
          drawer.innerHTML = freshDrawer.innerHTML;
        } else {
          current.innerHTML = fresh.innerHTML;
        }
      });
    },

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },

    async refreshCount() {
      const res = await fetch(`${theme.routes.cart}.js`);
      const cart = await res.json();
      this.updateCount(cart.item_count);
    },

    async add(form, button) {
      const errorEl = form.querySelector('[data-form-error]');
      if (errorEl) errorEl.hidden = true;
      button?.classList.add('is-loading');
      try {
        const body = new FormData(form);
        const ids = this.sectionIds();
        if (ids.length) body.append('sections', ids.join(','));
        body.append('sections_url', window.location.pathname);
        const res = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body,
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || theme.strings.cartError);
        }
        if (theme.cartType === 'page' || !document.querySelector('[data-drawer="cart"]')) {
          window.location.href = theme.routes.cart;
          return;
        }
        this.render(data.sections);
        await this.refreshCount();
        QuickView.close();
        openDrawer('cart');
      } catch (err) {
        if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
        else alert(err.message);
      } finally {
        button?.classList.remove('is-loading');
      }
    },

    async change(line, quantity) {
      document.querySelectorAll('[data-cart-contents]').forEach((el) => el.classList.add('is-loading'));
      try {
        const res = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
        this.render(data.sections);
        this.updateCount(data.item_count);
      } catch (err) {
        alert(err.message);
      } finally {
        document.querySelectorAll('[data-cart-contents]').forEach((el) => el.classList.remove('is-loading'));
      }
    },

    saveNote: debounce((note) => {
      fetch(`${theme.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
    }, 500),
  };

  class CartDrawer extends HTMLElement {}
  customElements.define('cart-drawer', CartDrawer);

  // Add to cart (product page + quick add on cards)
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const button = form.querySelector('[type="submit"]') || document.querySelector(`[form="${form.id}"][type="submit"]`);
    Cart.add(form, e.submitter || button);
  });

  // Quantity +/- buttons (product form + cart lines)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (btn) {
      const input = btn.closest('[data-qty]').querySelector('input');
      const min = parseInt(input.min || '0', 10);
      const next = Math.max(min, (parseInt(input.value, 10) || 0) + parseInt(btn.dataset.qtyChange, 10));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = e.target.closest('[data-line-remove]');
    if (remove) Cart.change(parseInt(remove.dataset.lineRemove, 10), 0);
  });

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-line-qty]')) {
      Cart.change(parseInt(e.target.dataset.lineQty, 10), parseInt(e.target.value, 10) || 0);
    }
  });
  document.addEventListener('input', (e) => {
    if (e.target.matches('[data-cart-note]')) Cart.saveNote(e.target.value);
  });

  /* ------------------------------------------------------------------
     Product page: variant picker, gallery, sticky add-to-cart
  ------------------------------------------------------------------ */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.form = this.querySelector('form[data-product-form]');
      this.isQuickView = this.dataset.section === 'quick-view';
      if (!this.isQuickView) RecentlyViewed.remember(this.dataset.url);
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-index]')) this.onOptionChange();
      });
      this.markUnavailable();
      this.initStickyBar();
    }

    selectedOptions() {
      return this.data.options.map((_, i) => {
        const checked = this.querySelector(`[data-option-index="${i}"]:checked`);
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      selected.forEach((value, i) => {
        const label = this.querySelector(`[data-option-label="${i}"]`);
        if (label) label.textContent = value;
      });
      const variant = this.data.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      this.markUnavailable();
      const addBtn = this.querySelector('[data-add-button]');
      const stickyBtn = document.querySelector('[data-sticky-add]');

      if (!variant) {
        if (addBtn) { addBtn.disabled = true; addBtn.querySelector('[data-add-label]').textContent = theme.strings.unavailable; }
        if (stickyBtn) { stickyBtn.disabled = true; stickyBtn.textContent = theme.strings.unavailable; }
        return;
      }

      this.querySelector('[data-variant-id]').value = variant.id;
      if (!this.isQuickView) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      if (variant.featured_media) this.gallery?.show(variant.featured_media.id);
      this.refreshFromServer(variant.id);
    }

    get gallery() { return this.querySelector('media-gallery'); }

    // Re-render price / stock / pickup / button via the Section Rendering API so money formatting stays server-side
    async refreshFromServer(variantId) {
      try {
        const res = await fetch(`${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.section}`);
        const doc = parseHTML(await res.text());
        ['[data-price-wrapper]', '[data-stock]', '[data-pickup-availability]', '[data-add-button]'].forEach((sel) => {
          const fresh = doc.querySelector(sel);
          const current = this.querySelector(sel);
          if (fresh && current) {
            current.innerHTML = fresh.innerHTML;
            if (sel === '[data-add-button]') current.disabled = fresh.disabled;
          }
        });
        if (this.isQuickView) return;
        const stickyPrice = document.querySelector('[data-sticky-price]');
        const freshSticky = doc.querySelector('[data-sticky-price]');
        if (stickyPrice && freshSticky) stickyPrice.innerHTML = freshSticky.innerHTML;
        const stickyBtn = document.querySelector('[data-sticky-add]');
        const freshStickyBtn = doc.querySelector('[data-sticky-add]');
        if (stickyBtn && freshStickyBtn) { stickyBtn.textContent = freshStickyBtn.textContent; stickyBtn.disabled = freshStickyBtn.disabled; }
      } catch (err) {
        console.error(err);
      }
    }

    // Cross out option values that don't combine into an available variant with the other current selections
    markUnavailable() {
      const selected = this.selectedOptions();
      this.data.options.forEach((_, i) => {
        this.querySelectorAll(`[data-option-index="${i}"]`).forEach((input) => {
          const available = this.data.variants.some((v) => v.available && v.options[i] === input.value &&
            v.options.every((opt, j) => j === i || opt === selected[j]));
          input.closest('.variant-pill').classList.toggle('is-unavailable', !available);
        });
      });
    }

    initStickyBar() {
      if (this.isQuickView) return;
      const bar = document.querySelector('[data-sticky-atc]');
      const target = this.querySelector('[data-add-button]');
      if (!bar || !target || !('IntersectionObserver' in window)) return;
      bar.hidden = false;
      new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(target);
    }
  }
  customElements.define('product-info', ProductInfo);

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-thumb]');
        if (thumb) this.show(thumb.dataset.thumb);
        const step = e.target.closest('[data-gallery-step]');
        if (step) this.step(parseInt(step.dataset.galleryStep, 10));
      });
      // Hover zoom follows the cursor
      this.addEventListener('mousemove', (e) => {
        const zoom = e.target.closest('[data-zoom]');
        if (!zoom) return;
        const r = zoom.getBoundingClientRect();
        zoom.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
        zoom.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
      });
      // Desktop: clicking the image doesn't navigate away (hover zoom is enough)
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-zoom]') && window.matchMedia('(hover: hover)').matches) e.preventDefault();
      });
    }
    step(dir) {
      const items = [...this.querySelectorAll('[data-media-id]')];
      if (!items.length) return;
      const current = items.findIndex((el) => el.classList.contains('is-active'));
      const next = items[(current + dir + items.length) % items.length];
      this.show(next.dataset.mediaId);
      this.querySelector('.thumbs__btn.is-active')?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    show(mediaId) {
      const id = String(mediaId);
      this.querySelectorAll('[data-media-id]').forEach((el) => el.classList.toggle('is-active', el.dataset.mediaId === id));
      this.querySelectorAll('[data-thumb]').forEach((el) => el.classList.toggle('is-active', el.dataset.thumb === id));
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;
      const load = async () => {
        this.dataset.loaded = 'true';
        const res = await fetch(this.dataset.url);
        const doc = parseHTML(await res.text());
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) { io.disconnect(); load(); }
        }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else load();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------
     Collection / search: filtering, sorting, infinite scroll
  ------------------------------------------------------------------ */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-facets-form]') || e.target.matches('[data-facet-sort]')) this.onFormChange();
      });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); this.fetchAndRender(link.href); }
      });
      window.addEventListener('popstate', () => this.fetchAndRender(window.location.href, false));
    }

    onFormChange = debounce(() => {
      const form = this.querySelector('[data-facets-form]');
      const params = new URLSearchParams(form ? new FormData(form) : undefined);
      // Drop empty price inputs so they don't filter to 0
      [...params.keys()].forEach((key) => { if (params.get(key) === '') params.delete(key); });
      const sort = this.querySelector('[data-facet-sort]');
      if (sort) params.set('sort_by', sort.value);
      const q = new URLSearchParams(window.location.search).get('q');
      if (q && !params.has('q')) { params.set('q', q); params.set('type', 'product'); }
      this.fetchAndRender(`${window.location.pathname}?${params.toString()}`);
    }, 400);

    async fetchAndRender(href, push = true) {
      const url = new URL(href, window.location.origin);
      const results = this.querySelector('[data-facet-results]');
      results?.classList.add('is-loading');
      try {
        const fetchUrl = new URL(url);
        fetchUrl.searchParams.set('section_id', this.dataset.section);
        const res = await fetch(fetchUrl);
        const doc = parseHTML(await res.text());
        ['[data-facet-results]', '[data-active-filters]', '.collection__count'].forEach((sel) => {
          const fresh = doc.querySelector(sel);
          const current = this.querySelector(sel);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        // Update filter counts but keep each <details> open/closed as the user left it
        const freshBodies = doc.querySelectorAll('.facet__body');
        this.querySelectorAll('.facet__body').forEach((body, i) => {
          if (freshBodies[i]) body.innerHTML = freshBodies[i].innerHTML;
        });
        if (push) window.history.pushState({}, '', url.pathname + url.search);
        initReveal(this);
      } catch (err) {
        console.error(err);
      } finally {
        this.querySelector('[data-facet-results]')?.classList.remove('is-loading');
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  class InfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.loading = false;
      this.querySelector('a')?.addEventListener('click', (e) => { e.preventDefault(); this.load(); });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) this.load(); }, { rootMargin: '0px 0px 300px 0px' });
        this.io.observe(this);
      }
    }
    disconnectedCallback() { this.io?.disconnect(); }
    async load() {
      if (this.loading) return;
      this.loading = true;
      this.querySelector('a')?.classList.add('is-loading');
      const res = await fetch(this.dataset.next);
      const doc = parseHTML(await res.text());
      const grid = this.parentElement.querySelector('.product-grid');
      doc.querySelectorAll('.product-grid > li').forEach((li) => grid.appendChild(document.adoptNode(li)));
      initReveal(grid);
      const nextLoader = doc.querySelector('infinite-scroll');
      if (nextLoader) this.replaceWith(document.adoptNode(nextLoader));
      else this.remove();
    }
  }
  customElements.define('infinite-scroll', InfiniteScroll);

  /* ------------------------------------------------------------------
     Recently viewed section
  ------------------------------------------------------------------ */
  class RecentlyViewedSection extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.loaded) return;
      this.dataset.loaded = 'true';
      const current = window.location.pathname.split('/products/')[1]?.split('/')[0];
      const handles = RecentlyViewed.list(current).slice(0, parseInt(this.dataset.limit, 10) || 6);
      if (!handles.length) return;
      const q = handles.map((h) => `handle:"${h}"`).join(' OR ');
      try {
        const res = await fetch(`${theme.routes.search}?q=${encodeURIComponent(q)}&type=product&section_id=${this.dataset.section}`);
        const doc = parseHTML(await res.text());
        const fresh = doc.querySelector('recently-viewed');
        if (!fresh || !fresh.querySelector('[data-handle]')) return;
        // Keep the order the customer actually viewed them in
        const track = fresh.querySelector('[data-slider-track]');
        handles.forEach((h) => { const li = track.querySelector(`[data-handle="${h}"]`); if (li) track.appendChild(li); });
        this.innerHTML = fresh.innerHTML;
        this.hidden = false;
      } catch (err) { console.error(err); }
    }
  }
  customElements.define('recently-viewed', RecentlyViewedSection);

  /* ------------------------------------------------------------------
     Promo popup
  ------------------------------------------------------------------ */
  class PromoPopup extends HTMLElement {
    connectedCallback() {
      this.key = `tundra:popup:${this.dataset.id}`;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-popup-close]') || e.target.classList.contains('modal')) this.close();
        const copy = e.target.closest('[data-copy]');
        if (copy) {
          navigator.clipboard?.writeText(copy.dataset.copy);
          copy.querySelector('small').textContent = theme.strings.copied;
        }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      // Keep it open after a successful newsletter submit (the page reloads with ?customer_posted=true)
      if (this.querySelector('.form-message--success')) { this.hidden = false; return; }
      if (this.hasAttribute('data-design-mode')) return;
      const days = parseInt(this.dataset.days, 10) || 0;
      const last = storage.get(this.key, 0);
      if (days > 0 && Date.now() - last < days * 864e5) return;
      setTimeout(() => { this.hidden = false; }, (parseInt(this.dataset.delay, 10) || 0) * 1000);
    }
    close() {
      if (this.hidden) return;
      this.hidden = true;
      storage.set(this.key, Date.now());
    }
  }
  customElements.define('promo-popup', PromoPopup);
  document.addEventListener('shopify:section:select', (e) => { const p = e.target.querySelector('promo-popup'); if (p) p.hidden = false; });
  document.addEventListener('shopify:section:deselect', (e) => { const p = e.target.querySelector('promo-popup'); if (p) p.hidden = true; });

  /* ------------------------------------------------------------------
     Before / after slider
  ------------------------------------------------------------------ */
  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      range?.addEventListener('input', () => this.style.setProperty('--pos', `${range.value}%`));
    }
  }
  customElements.define('before-after', BeforeAfter);

  // Close header dropdowns (language switcher) when clicking outside
  document.addEventListener('click', (e) => {
    document.querySelectorAll('.header-loc[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
  });

  /* ------------------------------------------------------------------
     Boot
  ------------------------------------------------------------------ */
  initReveal();
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
  // Theme editor: open the cart drawer when its section is selected
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.id === 'shopify-section-cart-drawer') openDrawer('cart');
  });
})();
